import * as React from 'react';
import { cn } from '#root/lib/utils';
import styles from './usd.module.scss';

/**
 * Monto en dólares sin el símbolo: "1,390", "1,199.50". Sin decimales si es entero; con 2 si no.
 * Acepta string porque los DECIMAL del backend llegan sin cast ("516.00").
 */
export const formatUsdAmount = (value: number | string): string => {
  const amount = Number(value);
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const hasUsdValue = (value: number | string | null | undefined): value is number | string =>
  value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value));

/**
 * Monto con símbolo para texto plano ("$1,390"): `aria-label`, mensajes. Sin valor, "".
 * Misma regla de vacío que `<Usd />`.
 */
export const usdText = (value: number | string | null | undefined): string =>
  hasUsdValue(value) ? `$${formatUsdAmount(value)}` : '';

export interface UsdProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Monto en dólares. Sin valor (`null`, `undefined`, vacío o no numérico) no pinta nada.
   */
  value: number | string | null | undefined;
}

/**
 * Usd Component - Design System
 *
 * Monto en dólares con el `$` más chico que el número, apenas separado y centrado en vertical,
 * como `.currency-usd` del admin. El `$` es texto real (se copia y lo lee el lector de
 * pantalla), no un `::before`. Solo dólares: para soles está `formatCurrency` de utils.
 * Tamaño y separación: tokens `--ds-usd-*`; va en `em`, así escala con el monto.
 *
 * @example
 * <strong><Usd value={1390} /></strong> // $1,390
 * <Usd value="516.50" /> // $516.50
 */
const Usd = React.forwardRef<HTMLSpanElement, UsdProps>(
  ({ value, className, ...props }, ref) => {
    if (!hasUsdValue(value)) {
      return null;
    }

    return (
      <span ref={ref} className={cn(styles.usd, className)} {...props}>
        <span className={styles.symbol}>$</span>
        {formatUsdAmount(value)}
      </span>
    );
  }
);

Usd.displayName = 'Usd';

export { Usd };
